import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TierStats } from '../../types';
import { TIER_COLORS, TIER_LABELS } from './tierChartConfig';

interface TierAverageCostChartProps {
  stats: TierStats[];
  title?: string;
}

function formatCost(amount: number): string {
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  if (amount < 1) return `$${amount.toFixed(3)}`;
  return `$${amount.toFixed(2)}`;
}

export function TierAverageCostChart({
  stats,
  title = 'Average Price by Tier',
}: TierAverageCostChartProps) {
  const data = stats
    .filter((item) => item.completedCount > 0)
    .map((item) => ({
      ...item,
      name: TIER_LABELS[item.tier],
      color: TIER_COLORS[item.tier],
    }));

  return (
    <div className="bg-white rounded-lg border border-surface-200 p-3 shadow-card h-full flex flex-col">
      <div className="flex items-center gap-2 mb-2 flex-shrink-0">
        <div className="w-7 h-7 rounded bg-emerald-100 flex items-center justify-center">
          <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h2 className="text-sm font-semibold text-surface-800">{title}</h2>
        <span className="ml-auto text-[10px] text-surface-500">
          Completed scans only
        </span>
      </div>

      {data.length > 0 ? (
        <div className="flex-1 min-h-0">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 12, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11, fill: '#64748b' }}
                interval={0}
              />
              <YAxis
                tick={{ fontSize: 9, fill: '#64748b' }}
                width={52}
                tickFormatter={(value: number) => formatCost(value)}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                }}
                formatter={(value: number) => [formatCost(value), 'Average Price']}
                labelFormatter={(label, payload) => {
                  if (payload && payload.length > 0) {
                    const item = payload[0].payload as (typeof data)[number];
                    return `${label} (${item.completedCount} completed scans)`;
                  }
                  return String(label);
                }}
              />
              <Bar dataKey="averageCost" radius={[4, 4, 0, 0]}>
                {data.map((item) => (
                  <Cell key={item.tier} fill={item.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex items-center justify-center text-surface-400">
          <div className="text-center">
            <p>No tier cost data available</p>
            <p className="text-sm mt-1">Completed scans are required</p>
          </div>
        </div>
      )}
    </div>
  );
}
